import type { Lead } from '@/types';

/**
 * Advanced platform detection using referrer domain parsing + UTM data
 * Priority order:
 * 1. UTM medium/source (explicit campaign tracking)
 * 2. Referrer domain analysis (organic traffic detection)
 */
export function detectPlatform(
  lead: Lead | Record<string, unknown>
): string {
  const l = lead as Record<string, unknown>;
  
  // Get UTM fields
  const utmMedium = String(l.utm_medium || '').toLowerCase().trim();
  const utmSource = String(l.utm_source || '').toLowerCase().trim();
  
  // Get referrer and parse domain
  const referrer = String(l.referrer || '').toLowerCase();
  const referrerDomain = extractDomain(referrer);
  
  // ─── Step 1: Check UTM Medium (highest priority for paid/organic) ───
  
  // Paid detection via UTM medium
  if (utmMedium.includes('cpc') || 
      utmMedium.includes('ppc') || 
      utmMedium.includes('paid') ||
      utmMedium.includes('cpm') ||
      utmMedium.includes('sponsored') ||
      utmMedium.includes('display') ||
      utmMedium.includes('retargeting') ||
      utmMedium.includes('remarketing')) {
    // Determine which paid platform
    if (utmSource.includes('google') || 
        utmSource.includes('gsearch') || 
        referrerDomain.includes('google')) return 'Google Ads';
    if (utmSource.includes('meta') || 
        utmSource.includes('facebook') || 
        utmSource.includes('fb') ||
        referrerDomain.includes('facebook')) return 'Meta Ads';
    if (utmSource.includes('instagram') || 
        utmSource.includes('ig') ||
        referrerDomain.includes('instagram')) return 'Instagram Ads';
    if (utmSource.includes('linkedin') || 
        utmSource.includes('li') ||
        referrerDomain.includes('linkedin')) return 'LinkedIn Ads';
    if (utmSource.includes('youtube') || 
        utmSource.includes('yt') ||
        referrerDomain.includes('youtube')) return 'YouTube Ads';
    return 'Paid';
  }
  
  // Social/organic detection via UTM medium
  if (utmMedium.includes('social') || 
      utmMedium.includes('organic_social') ||
      utmMedium.includes('community')) {
    if (utmSource.includes('instagram') || utmSource.includes('ig')) return 'Instagram';
    if (utmSource.includes('linkedin') || utmSource.includes('li')) return 'LinkedIn';
    if (utmSource.includes('facebook') || utmSource.includes('fb')) return 'Facebook';
    if (utmSource.includes('twitter') || utmSource.includes('x')) return 'Twitter/X';
    if (utmSource.includes('youtube') || utmSource.includes('yt')) return 'YouTube';
    if (utmSource.includes('tiktok')) return 'TikTok';
    return 'Social';
  }
  
  // Email detection
  if (utmMedium.includes('email') || 
      utmMedium.includes('newsletter') ||
      utmMedium.includes('edm')) {
    return 'Email';
  }
  
  // Affiliate detection
  if (utmMedium.includes('affiliate') || 
      utmMedium.includes('partner') ||
      utmMedium.includes('referral')) {
    return 'Affiliate';
  }
  
  // ─── Step 2: Check UTM Source ───
  
  if (utmSource.includes('instagram') || utmSource.includes('ig')) return 'Instagram';
  if (utmSource.includes('linkedin') || utmSource.includes('li')) return 'LinkedIn';
  if (utmSource.includes('facebook') || utmSource.includes('fb')) return 'Facebook';
  if (utmSource.includes('twitter') || utmSource.includes('x.com')) return 'Twitter/X';
  if (utmSource.includes('youtube') || utmSource.includes('yt')) return 'YouTube';
  if (utmSource.includes('tiktok')) return 'TikTok';
  if (utmSource.includes('google')) return 'Google';
  if (utmSource.includes('bing')) return 'Bing';
  if (utmSource.includes('direct') || utmSource === '(direct)') return 'Direct';
  
  // ─── Step 3: Check Referrer Domain (for organic/untagged traffic) ───
  
  if (referrerDomain) {
    // Social platforms
    if (referrerDomain.includes('instagram.com')) return 'Instagram';
    if (referrerDomain.includes('linkedin.com')) return 'LinkedIn';
    if (referrerDomain.includes('facebook.com') || referrerDomain.includes('fb.com')) return 'Facebook';
    if (referrerDomain.includes('twitter.com') || referrerDomain.includes('x.com')) return 'Twitter/X';
    if (referrerDomain.includes('youtube.com') || referrerDomain.includes('youtu.be')) return 'YouTube';
    if (referrerDomain.includes('tiktok.com')) return 'TikTok';
    if (referrerDomain.includes('pinterest.com')) return 'Pinterest';
    if (referrerDomain.includes('reddit.com')) return 'Reddit';
    
    // Search engines
    if (referrerDomain.includes('google.')) return 'Google';
    if (referrerDomain.includes('bing.com')) return 'Bing';
    if (referrerDomain.includes('yahoo.com')) return 'Yahoo';
    if (referrerDomain.includes('duckduckgo.com')) return 'DuckDuckGo';
    
    // Design/Creative platforms
    if (referrerDomain.includes('behance.net')) return 'Behance';
    if (referrerDomain.includes('dribbble.com')) return 'Dribbble';
    if (referrerDomain.includes('pinterest.')) return 'Pinterest';
    
    // Messaging
    if (referrerDomain.includes('whatsapp.com')) return 'WhatsApp';
    if (referrerDomain.includes('telegram.org') || referrerDomain.includes('t.me')) return 'Telegram';
    
    // If referrer exists but isn't in our list, it's some other website
    if (referrerDomain && !referrerDomain.includes(window?.location?.hostname || '')) {
      return 'Referral';
    }
  }
  
  // ─── Step 4: Default cases ───
  
  // If no UTM and no referrer = Direct traffic
  if ((!utmMedium || utmMedium === '') && (!utmSource || utmSource === '') && !referrer) {
    return 'Direct';
  }
  
  // If UTM says organic or empty medium with source
  if (utmMedium === 'organic' || utmMedium === '' || utmMedium === 'null') {
    if (utmSource && utmSource !== '') return 'Organic';
  }
  
  return 'Other';
}

/**
 * Extract clean domain from URL
 */
function extractDomain(url: string): string {
  if (!url || url === '' || url === 'null' || url === 'undefined') return '';
  
  try {
    // Handle cases where URL doesn't have protocol
    let cleanUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      cleanUrl = 'https://' + url;
    }
    
    const urlObj = new URL(cleanUrl);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    // Fallback: manual extraction
    const match = url.match(/^(?:https?:\/\/)?(?:www\.)?([^/]+)/i);
    return match ? match[1].toLowerCase() : '';
  }
}

/**
 * Group detailed platforms into broader categories for charts
 */
export function getPlatformCategory(detailedPlatform: string): string {
  const categories: Record<string, string> = {
    'Google Ads': 'Paid',
    'Meta Ads': 'Paid',
    'Instagram Ads': 'Paid',
    'LinkedIn Ads': 'Paid',
    'YouTube Ads': 'Paid',
    'Paid': 'Paid',
    'Google': 'Organic',
    'Bing': 'Organic',
    'Yahoo': 'Organic',
    'DuckDuckGo': 'Organic',
    'Organic': 'Organic',
    'Direct': 'Organic',
    'Instagram': 'Instagram',
    'LinkedIn': 'LinkedIn',
    'Facebook': 'Social',
    'Twitter/X': 'Social',
    'YouTube': 'Social',
    'TikTok': 'Social',
    'Pinterest': 'Social',
    'Reddit': 'Social',
    'Behance': 'Social',
    'Dribbble': 'Social',
    'Social': 'Social',
    'WhatsApp': 'Other',
    'Telegram': 'Other',
    'Email': 'Other',
    'Affiliate': 'Other',
    'Referral': 'Other',
  };
  
  return categories[detailedPlatform] || 'Other';
}

/**
 * Get platform color for consistent styling
 */
export function getPlatformColor(platform: string): string {
  const colors: Record<string, string> = {
    'Google Ads': '#4285F4',
    'Meta Ads': '#1877F2',
    'Instagram Ads': '#E4405F',
    'LinkedIn Ads': '#0077B5',
    'YouTube Ads': '#FF0000',
    'Paid': '#C40233',
    'Google': '#34A853',
    'Organic': '#127852',
    'Direct': '#6366F1',
    'Instagram': '#FF1981',
    'LinkedIn': '#0077B5',
    'Facebook': '#1877F2',
    'Twitter/X': '#000000',
    'YouTube': '#FF0000',
    'TikTok': '#000000',
    'Pinterest': '#BD081C',
    'Reddit': '#FF4500',
    'Social': '#8B5CF6',
    'Behance': '#1769FF',
    'Dribbble': '#EA4C89',
    'Referral': '#F59E0B',
    'WhatsApp': '#25D366',
    'Telegram': '#0088CC',
    'Email': '#F59E0B',
    'Affiliate': '#10B981',
    'Other': '#6B7280',
  };
  
  return colors[platform] || '#6B7280';
}

/**
 * Get category color for charts
 */
export function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    'Paid': '#C40233',
    'Organic': '#127852',
    'Instagram': '#FF1981',
    'LinkedIn': '#0077B5',
    'Social': '#8B5CF6',
    'Other': '#6B7280',
  };
  
  return colors[category] || '#6B7280';
}
